"use client"

import * as React from "react"
import { type ColumnDef } from "@tanstack/react-table"

import { useTranslations } from "@/i18n"
import { Badge, DataTable } from "@ibada/ui"
import { formatMoney } from "@/features/finance/lib/finance-utils"
import { useFinancePageState } from "@/features/finance/hooks/use-finance-page-state"
import { FinanceCategoryScope } from "@/features/finance/model/finance-types"
import { useAccountingChartQuery } from "@/features/finance/services/finance-query"
import { InvestigateButton } from "@/features/finance/components/audit/investigate-button"
import {
  AccountingAccountType,
  CONTRA_CLASSIFICATIONS,
  type ChartAccount,
} from "@/features/finance/model/accounting-reports-types"
import { useBranchCurrency } from "@/features/finance/hooks/use-branch-currency"

const accountTypeBadgeClasses: Record<AccountingAccountType, string> = {
  [AccountingAccountType.Asset]:
    "border-sky-500/30 bg-sky-500/15 text-sky-700 dark:border-sky-400/30 dark:bg-sky-400/20 dark:text-sky-200",
  [AccountingAccountType.Liability]:
    "border-violet-500/30 bg-violet-500/15 text-violet-700 dark:border-violet-400/30 dark:bg-violet-400/20 dark:text-violet-200",
  [AccountingAccountType.NetAsset]:
    "border-amber-500/30 bg-amber-500/15 text-amber-700 dark:border-amber-400/30 dark:bg-amber-400/20 dark:text-amber-200",
  [AccountingAccountType.Income]:
    "border-emerald-500/30 bg-emerald-500/15 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-400/20 dark:text-emerald-200",
  [AccountingAccountType.Expense]:
    "border-rose-500/30 bg-rose-500/15 text-rose-700 dark:border-rose-400/30 dark:bg-rose-400/20 dark:text-rose-200",
}

export function AccountingChartPageContent() {
  const t = useTranslations("finance.accounting")
  const { workspaceId, activeContext, scopeBranch } = useFinancePageState()
  const branchId = scopeBranch?.id ?? null
  const baseCurrency = useBranchCurrency()
  const chartQuery = useAccountingChartQuery(
    workspaceId && (activeContext === "parish" || branchId)
      ? {
          workspaceId,
          branchId,
          scope: activeContext === "branch" ? FinanceCategoryScope.Branch : FinanceCategoryScope.Parish,
        }
      : null,
  )
  const items = chartQuery.data?.items ?? []

  const columns = React.useMemo<ColumnDef<ChartAccount>[]>(
    () => [
      {
        accessorKey: "code",
        header: t("columns.code"),
        cell: ({ row }) => <span className='font-mono tabular-nums'>{row.original.code}</span>,
      },
      {
        accessorKey: "name",
        header: t("columns.account"),
        // Localized by the API (ADR-0030).
        cell: ({ row }) => row.original.name,
      },
      {
        accessorKey: "type",
        header: t("columns.type"),
        cell: ({ row }) => (
          <Badge
            variant='outline'
            className={`${accountTypeBadgeClasses[row.original.type]} !bg-transparent shadow-none`}
          >
            {t(`types.${row.original.type}`)}
          </Badge>
        ),
      },
      {
        accessorKey: "classification",
        header: t("columns.classification"),
        cell: ({ row }) => (
          <span className='text-muted-foreground'>{t(`classifications.${row.original.classification}`)}</span>
        ),
      },
      {
        accessorKey: "normalBalance",
        header: t("columns.normalBalance"),
        cell: ({ row }) => (
          <span
            className={
              // A contra-account sits on the opposite side to its type's natural balance (an ASSET that
              // credits). Worth calling out — it reads like a mistake otherwise.
              CONTRA_CLASSIFICATIONS.includes(row.original.classification)
                ? "text-amber-700 dark:text-amber-300"
                : "text-muted-foreground"
            }
          >
            {t(`directions.${row.original.normalBalance}`)}
          </span>
        ),
      },
      {
        accessorKey: "balance",
        header: () => <div className='text-right'>{t("columns.balance")}</div>,
        cell: ({ row }) => (
          <div className='text-right font-mono tabular-nums'>{formatMoney(row.original.balance, baseCurrency)}</div>
        ),
      },
      {
        id: "investigate",
        header: "",
        cell: ({ row }) => (
          <div className='text-right'>
            <InvestigateButton
              context={{
                accountingAccountId: row.original.id,
                label: row.original.name,
              }}
              iconOnly
              size='icon-sm'
            />
          </div>
        ),
      },
    ],
    [baseCurrency, t],
  )

  return (
    <DataTable data={items} columns={columns} isLoading={chartQuery.isLoading} emptyMessage={t("empty.chart")} />
  )
}
